import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

export function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(dateString))
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

// Device activity reports render times in the server's report timezone (an
// IANA name returned with every report response), not the browser's zone, so
// a row's time always agrees with its Date column and with the date filters.
dayjs.extend(utc)
dayjs.extend(timezone)

export const REPORT_DATE_FORMAT = 'YYYY-MM-DD'
export const REPORT_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/** True if `tz` is an IANA zone this runtime can format in. */
export function isValidTimeZone(tz: string | undefined): tz is string {
  if (!tz) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** Formats an ISO-8601 instant in `tz` (UTC if missing/unknown). Empty input → ''. */
export function formatInTz(iso: string | undefined | null, tz: string | undefined, format = REPORT_DATE_TIME_FORMAT): string {
  if (!iso) return ''
  const d = dayjs(iso)
  if (!d.isValid()) return ''
  return d.tz(isValidTimeZone(tz) ? tz : 'UTC').format(format)
}

/** The current moment in `tz` (UTC if missing/unknown). */
export function nowInTz(tz: string | undefined): Dayjs {
  return dayjs().tz(isValidTimeZone(tz) ? tz : 'UTC')
}

/** Display label for a lock/unlock report action. */
export function actionLabel(action: string): string {
  return action === 'LOCK' ? 'Lock' : action === 'UNLOCK' ? 'Unlock' : action
}
